import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { imgSrc } from '../lib/imgSrc';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import AddLocationDialog from '../components/AddLocationDialog';
import EditLocationDialog from '../components/EditLocationDialog';
import AddMachineryDialog from '../components/AddMachineryDialog';
import QuickMaintenanceDialog from '../components/QuickMaintenanceDialog';
import { WorkOrderOriginLink } from '../components/WorkOrderOriginLink';
import SubmitMaintenanceRequestDialog from '../components/SubmitMaintenanceRequestDialog';
import UnaddressedRequestsBanner from '../components/UnaddressedRequestsBanner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Settings, ClipboardList, History, Package, Users as UsersIcon, LogOut } from 'lucide-react';
import HeaderMenu from '../components/HeaderMenu';
import PinButton from '../components/PinButton';
import { usePins } from '../lib/PinsContext';
import { getPriorityLabel, getPriorityBadgeClass } from '../lib/priority';

const Dashboard = ({ user, onLogout }) => {
  const [locations, setLocations] = useState([]);
  const [unassignedEquipment, setUnassignedEquipment] = useState([]);
  const [stats, setStats] = useState(null);
  const [upcomingWOs, setUpcomingWOs] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [pinnedEquipment, setPinnedEquipment] = useState([]);
  const [resolvingFollowUp, setResolvingFollowUp] = useState(null);
  const { setPinnedFromArray } = usePins();
  const [loading, setLoading] = useState(true);
  const [showAddLocationDialog, setShowAddLocationDialog] = useState(false);
  const [showEditLocationDialog, setShowEditLocationDialog] = useState(false);
  const [showAddEquipmentDialog, setShowAddEquipmentDialog] = useState(false);
  const [showQuickMaintenanceDialog, setShowQuickMaintenanceDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const navigate = useNavigate();

  const isAdmin = user.role === 'admin' || user.role === 'technician';
  const canSubmitRequest = isAdmin || user.role === 'operator';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [locationsRes, statsRes, unassignedRes, upcomingRes, followUpsRes, pinsRes] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/machinery/unassigned`),
        axios.get(`${API}/work-orders/upcoming?days=28`),
        axios.get(`${API}/follow-ups?status=open`),
        axios.get(`${API}/equipment-pins`),
      ]);
      setLocations(locationsRes.data);
      setStats(statsRes.data);
      setUnassignedEquipment(unassignedRes.data);
      setUpcomingWOs(upcomingRes.data);
      setFollowUps(followUpsRes.data);
      setPinnedEquipment(pinsRes.data);
      setPinnedFromArray(pinsRes.data.map((m) => m.id));
      setPinnedEquipment(pinsRes.data);
      setPinnedFromArray(pinsRes.data.map((m) => m.id));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLocation = (location, e) => {
    e.stopPropagation();
    setSelectedLocation(location);
    setShowEditLocationDialog(true);
  };

  const handleDeleteLocation = async (locationId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.delete(`${API}/locations/${locationId}`);
      toast.success(response.data.message || 'Location deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      operational: { label: 'Operational', className: 'bg-green-500 hover:bg-green-600' },
      maintenance_needed: { label: 'Maintenance Needed', className: 'bg-yellow-500 hover:bg-yellow-600' },
      down: { label: 'Down', className: 'bg-red-500 hover:bg-red-600' },
      lockout: { label: 'Lockout', className: 'bg-orange-500 hover:bg-orange-600' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img
                src="/ih-logo.png"
                alt="Independence Hydrogen"
                className="h-12 w-12 sm:h-14 sm:w-14 object-contain shrink-0"
                data-testid="dashboard-logo"
              />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">H2 Machinery Manager</h1>
                <p className="text-sm text-slate-600 mt-1">
                  Welcome, {user.name} ({user.role})
                </p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {canSubmitRequest && (
                <Button onClick={() => setShowRequestDialog(true)} data-testid="submit-request-btn" variant="default">
                  <span className="sm:hidden">+ Request</span>
                  <span className="hidden sm:inline">+ Submit Maintenance Request</span>
                </Button>
              )}
              <HeaderMenu user={user} onLogout={onLogout} showHomeLink={false} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <UnaddressedRequestsBanner />

        {/* Pinned Equipment strip — user's one-tap shortcuts, rendered above
             everything else so it's the very first thing they see. */}
        {pinnedEquipment.length > 0 && (
          <div className="mb-6" data-testid="pinned-equipment-strip">
            <div className="flex items-center gap-2 mb-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-amber-500" aria-hidden="true">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Pinned Equipment
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {pinnedEquipment.map((m) => {
                const goToDetail = () => navigate(`/machinery/${m.id}`);
                return (
                  <div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={goToDetail}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToDetail();
                      }
                    }}
                    data-testid={`pinned-tile-${m.id}`}
                    className="inline-flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg border bg-white hover:shadow-md hover:border-amber-300 transition text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-slate-900 truncate max-w-[220px]">
                        {m.component}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-[220px]">
                        {m.location_name}
                      </div>
                    </div>
                    <PinButton
                      machineryId={m.id}
                      pinned={true}
                      onChange={(_next, ids) => {
                        setPinnedFromArray(ids);
                        setPinnedEquipment((prev) => prev.filter((x) => ids.includes(x.id)));
                      }}
                      size="sm"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Card data-testid="stat-total">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total Equipment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.total_machinery}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-operational">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Operational</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{stats.operational}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-maintenance">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Needs Maintenance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600">{stats.maintenance_needed}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-down">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Down</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">{stats.down}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-lockout">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Lockout</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{stats.lockout}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Locations/Plants — moved to top for faster navigation */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Plant Locations</h2>
            {isAdmin && (
              <Button onClick={() => setShowAddLocationDialog(true)} data-testid="add-location-btn">
                Add Location
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location) => (
              <Card
                key={location.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                data-testid={`location-card-${location.id}`}
              >
                <CardHeader>
                  <CardTitle className="text-2xl">{location.name}</CardTitle>
                  {location.description && (
                    <p className="text-slate-600 mt-2">{location.description}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    className="w-full"
                    onClick={() => navigate(`/location/${location.id}`)}
                    data-testid={`view-equipment-btn-${location.id}`}
                  >
                    View Equipment →
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/location/${location.id}/maintenance-logs`)}
                    data-testid={`maintenance-logs-btn-${location.id}`}
                  >
                    Maintenance Logs →
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/location/${location.id}/maintenance-requests`)}
                    data-testid={`maintenance-requests-btn-${location.id}`}
                  >
                    Maintenance Requests →
                  </Button>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => handleEditLocation(location, e)}
                        data-testid={`edit-location-btn-${location.id}`}
                      >
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`delete-location-btn-${location.id}`}
                          >
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Location?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will delete the location. Any equipment in this location will be moved to &quot;Unassigned Equipment&quot;.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => handleDeleteLocation(location.id, e)}
                              data-testid={`confirm-delete-location-btn-${location.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {locations.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-slate-600 text-lg mb-4">No locations yet</p>
              {isAdmin && (
                <Button onClick={() => setShowAddLocationDialog(true)}>
                  Add Your First Location
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Required Follow-Ups (from maintenance logs, priority-sorted) */}
        {followUps.length > 0 && (
          <Card className="mb-6" data-testid="required-follow-ups-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Required Follow-Ups</span>
                <span className="text-sm font-normal text-slate-500">
                  {followUps.length} open · sorted by priority
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {followUps.map((fu) => {
                  const dueMs = fu.due_date ? new Date(fu.due_date).getTime() : null;
                  const daysUntilDue = dueMs == null
                    ? null
                    : Math.ceil((dueMs - Date.now()) / (1000 * 60 * 60 * 24));
                  const dueLabel = (() => {
                    if (daysUntilDue == null) return '';
                    if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)}d overdue`;
                    if (daysUntilDue === 0) return 'Due today';
                    if (daysUntilDue === 1) return 'Due tomorrow';
                    return `Due in ${daysUntilDue}d`;
                  })();
                  const dueToneClass = daysUntilDue == null
                    ? 'text-slate-500'
                    : daysUntilDue < 0
                    ? 'text-red-700 font-medium'
                    : daysUntilDue <= 3
                    ? 'text-amber-700'
                    : 'text-slate-500';
                  const isLinked = !!fu.linked_work_order_id;

                  return (
                    <div
                      key={fu.id}
                      className="flex flex-wrap items-start justify-between gap-3 p-3 rounded-md border bg-white hover:shadow-sm transition"
                      data-testid={`follow-up-${fu.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {fu.priority && (
                            <Badge className={getPriorityBadgeClass(fu.priority)}>
                              {getPriorityLabel(fu.priority)}
                            </Badge>
                          )}
                          <div className="font-medium truncate">{fu.description}</div>
                        </div>
                        <div className="text-xs text-slate-600 mt-1 truncate">
                          {fu.equipment_name || 'Equipment'}
                          {isLinked && ' · linked to work order (auto-resolves on completion)'}
                        </div>
                        <WorkOrderOriginLink record={fu} testId={`follow-up-origin-${fu.id}`} />
                        {fu.source_request_id && (
                          <div className="text-xs text-slate-500 mt-0.5" data-testid={`follow-up-request-origin-${fu.id}`}>
                            From maintenance request{fu.source_request_submitted_by_name ? ` by ${fu.source_request_submitted_by_name}` : ''}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <div className={`text-xs ${dueToneClass}`}>{dueLabel}</div>
                        {isLinked && <Button size="sm" variant="outline" data-testid={`open-follow-up-wo-${fu.id}`}
                          onClick={() => navigate(`/machinery/${fu.source_machinery_id}?workOrder=${fu.linked_work_order_id}`)}>View work order</Button>}
                        {isAdmin && !isLinked && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setResolvingFollowUp(fu)}
                            data-testid={`resolve-follow-up-btn-${fu.id}`}
                          >
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Maintenance (work orders due within 28 days) */}
        {upcomingWOs.length > 0 && (
          <Card className="mb-8" data-testid="upcoming-maintenance-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Upcoming Maintenance</span>
                <span className="text-sm font-normal text-slate-500">
                  Next 4 weeks · {upcomingWOs.length} work order{upcomingWOs.length === 1 ? '' : 's'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {upcomingWOs.map((wo) => {
                  // Color rules:
                  //   - in_progress / completed → no color
                  //   - else within 7 days (or overdue) → gentle red
                  //   - else within 14 days → gentle yellow
                  //   - else → no color
                  // Effective due = earlier of calendar due date vs hours-based
                  // projection (whichever comes first drives urgency).
                  const effDue = wo.effective_due_date || wo.due_date;
                  const dueMs = effDue ? new Date(effDue).getTime() : null;
                  const hoursDriven = !!(wo.projected_hours_due_date && (!wo.due_date ||
                    new Date(wo.projected_hours_due_date).getTime() < new Date(wo.due_date).getTime()));
                  const daysUntilDue = dueMs == null
                    ? null
                    : Math.ceil((dueMs - Date.now()) / (1000 * 60 * 60 * 24));
                  const isQuiet = wo.status === 'in_progress' || wo.status === 'completed';
                  let toneClass = 'bg-white border-slate-200';
                  let labelToneClass = 'text-slate-700';
                  if (!isQuiet && daysUntilDue != null) {
                    if (daysUntilDue <= 7) {
                      toneClass = 'bg-red-50 border-red-200';
                      labelToneClass = 'text-red-800';
                    } else if (daysUntilDue <= 14) {
                      toneClass = 'bg-amber-50 border-amber-200';
                      labelToneClass = 'text-amber-800';
                    }
                  }
                  const dueLabel = (() => {
                    if (daysUntilDue == null) return '';
                    if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)}d overdue`;
                    if (daysUntilDue === 0) return 'Due today';
                    if (daysUntilDue === 1) return 'Due tomorrow';
                    return `Due in ${daysUntilDue}d`;
                  })();
                  return (
                    <div
                      key={wo.id}
                      className={`flex items-center justify-between gap-3 p-3 rounded-md border cursor-pointer hover:shadow-sm transition ${toneClass}`}
                      onClick={() => navigate(`/machinery/${wo.machinery_id}`)}
                      data-testid={`upcoming-wo-${wo.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          {wo.wo_number && (
                            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                              #{wo.wo_number}
                            </span>
                          )}
                          <div className="font-medium truncate">{wo.title}</div>
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5 truncate">
                          {wo.equipment_name}
                          {wo.frequency_days ? ` · Recurs every ${wo.frequency_days}d` : ''}
                          {wo.frequency_hours ? ` · Every ${Number(wo.frequency_hours).toLocaleString()} run hrs` : ''}
                          {wo.hours_due_at != null ? ` · Due at ${Number(wo.hours_due_at).toLocaleString()} hrs` : ''}
                        </div>
                      </div>
                      <div className="flex flex-col items-end shrink-0 text-right">
                        <span className={`text-sm font-medium ${labelToneClass}`}>
                          {dueLabel}{hoursDriven ? ' · by hours' : ''}
                        </span>
                        <span className="text-xs text-slate-500 mt-0.5">
                          {wo.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unassigned Equipment Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Unassigned Equipment</h2>
            {isAdmin && (
              <Button onClick={() => setShowAddEquipmentDialog(true)} data-testid="add-unassigned-equipment-btn">
                Add Equipment
              </Button>
            )}
          </div>
          
          {unassignedEquipment.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {unassignedEquipment.map((m) => (
                <Card
                  key={m.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/machinery/${m.id}`)}
                  data-testid={`unassigned-equipment-${m.id}`}
                >
                  {m.main_image && (
                    <div className="h-48 overflow-hidden rounded-t-lg">
                      <img src={imgSrc(m.main_image, { thumb: true })} alt={m.component} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{m.component}</CardTitle>
                      {getStatusBadge(m.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Local Location:</span>
                        <span className="font-medium">{m.location}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Manufacturer:</span>
                        <span className="font-medium">{m.manufacturer}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Serial #:</span>
                        <span className="font-medium font-mono text-xs">{m.serial_number}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-white rounded-lg border border-dashed border-slate-300">
              <p className="text-slate-500">No unassigned equipment</p>
              {isAdmin && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowAddEquipmentDialog(true)}
                >
                  Add Equipment
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      {isAdmin && (
        <>
          <AddLocationDialog
            open={showAddLocationDialog}
            onClose={() => setShowAddLocationDialog(false)}
            onSuccess={() => {
              setShowAddLocationDialog(false);
              fetchData();
            }}
          />
          <EditLocationDialog
            open={showEditLocationDialog}
            onClose={() => {
              setShowEditLocationDialog(false);
              setSelectedLocation(null);
            }}
            location={selectedLocation}
            onSuccess={() => {
              setShowEditLocationDialog(false);
              setSelectedLocation(null);
              fetchData();
            }}
          />
          <AddMachineryDialog
            open={showAddEquipmentDialog}
            onClose={() => setShowAddEquipmentDialog(false)}
            plantLocationId={null}
            locations={locations}
            onSuccess={() => {
              setShowAddEquipmentDialog(false);
              fetchData();
            }}
          />
          <QuickMaintenanceDialog
            open={showQuickMaintenanceDialog}
            onClose={() => setShowQuickMaintenanceDialog(false)}
            onSuccess={() => {
              setShowQuickMaintenanceDialog(false);
              fetchData();
            }}
          />
          <QuickMaintenanceDialog
            open={!!resolvingFollowUp}
            resolvesFollowUp={resolvingFollowUp}
            onClose={() => setResolvingFollowUp(null)}
            onSuccess={() => {
              setResolvingFollowUp(null);
              fetchData();
            }}
          />
        </>
      )}
      {canSubmitRequest && (
        <SubmitMaintenanceRequestDialog
          open={showRequestDialog}
          onClose={() => setShowRequestDialog(false)}
          onSuccess={() => {
            setShowRequestDialog(false);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
